import { useNavigate } from 'react-router-dom';
import SeoHead from '../../components/SeoHead';

const sections = [
  {
    id: 1,
    icon: 'ri-group-line',
    color: 'orange',
    title: 'Horizontalidad y cultura del consenso (Poldermodel)',
    content: [
      {
        subtitle: 'El modelo polder: decisiones por consenso',
        text: 'Los Países Bajos son famosos por su "poldermodel": un sistema de toma de decisiones basado en el consenso entre todas las partes implicadas. En el trabajo, esto significa que las decisiones importantes se debaten en equipo antes de implementarse. No esperes que el jefe decida unilateralmente; tu opinión como empleado es genuinamente valorada.',
      },
      {
        subtitle: 'Jerarquía plana',
        text: 'Las estructuras organizativas holandesas son notablemente horizontales. Es habitual llamar al director por su nombre de pila desde el primer día. No hay grandes diferencias de trato entre niveles jerárquicos. Esta horizontalidad no significa falta de estructura, sino respeto mutuo y comunicación abierta en todos los niveles.',
      },
      {
        subtitle: 'Reuniones y participación activa',
        text: 'Las reuniones en los Países Bajos son frecuentes y se espera que todos los participantes contribuyan activamente. Llegar sin opinión formada o sin haber preparado el tema se percibe negativamente. Las reuniones tienen agenda previa y se espera que sean eficientes. El silencio puede interpretarse como desacuerdo o desinterés.',
      },
    ],
  },
  {
    id: 2,
    icon: 'ri-speak-line',
    color: 'emerald',
    title: 'Comunicación directa y franqueza',
    content: [
      {
        subtitle: 'La franqueza holandesa (Directheid)',
        text: 'Los holandeses son conocidos por su comunicación extremadamente directa. Un colega puede decirte sin rodeos que tu presentación no es buena o que tu idea no funciona. No lo tomes como un ataque personal; es simplemente su forma de comunicarse. Esta franqueza también significa que cuando dicen algo positivo, lo dicen en serio.',
      },
      {
        subtitle: 'Feedback constructivo y continuo',
        text: 'La cultura del feedback es muy fuerte en los Países Bajos. Se espera que des y recibas feedback de forma regular, no solo en las evaluaciones anuales. Aprende a dar feedback de forma constructiva y a recibirlo sin ponerte a la defensiva. Esta práctica mejora enormemente la calidad del trabajo en equipo.',
      },
      {
        subtitle: 'Humor e informalidad',
        text: 'El ambiente laboral holandés es relativamente informal y el humor es bienvenido. Los holandeses tienen un sentido del humor irónico y autodespreciativo. No te sorprendas si hay bromas en reuniones o si el tono es más relajado de lo que esperabas. Esta informalidad coexiste con un alto nivel de profesionalidad y exigencia.',
      },
    ],
  },
  {
    id: 3,
    icon: 'ri-scales-3-line',
    color: 'sky',
    title: 'Equilibrio vida-trabajo (Work-life balance)',
    content: [
      {
        subtitle: 'El trabajo a tiempo parcial es normal',
        text: 'Los Países Bajos tienen la tasa más alta de trabajo a tiempo parcial de Europa. Trabajar 4 días a la semana (32 horas) es completamente normal y aceptado en todos los niveles, incluyendo puestos directivos. No se percibe como falta de compromiso; es una elección de vida respetada. Muchos profesionales altamente cualificados trabajan en este régimen.',
      },
      {
        subtitle: 'Respeto por el tiempo personal',
        text: 'Fuera del horario laboral, los holandeses desconectan completamente. No se espera que respondas emails o mensajes fuera de tu jornada. Las vacaciones son sagradas: 20 días mínimo por ley, y la mayoría de empresas ofrecen 25. Durante las vacaciones, nadie te molesta. Este respeto por el tiempo personal es bidireccional.',
      },
      {
        subtitle: 'Conciliación familiar',
        text: 'Los Países Bajos tienen políticas de conciliación familiar muy avanzadas. El permiso de paternidad se ha ampliado significativamente. Las guarderías y el sistema educativo son de alta calidad. Muchas empresas ofrecen horarios flexibles y posibilidad de teletrabajo. La familia es una prioridad reconocida y respetada en el entorno laboral.',
      },
    ],
  },
  {
    id: 4,
    icon: 'ri-bicycle-line',
    color: 'amber',
    title: 'Cultura práctica y pragmatismo',
    content: [
      {
        subtitle: 'El pragmatismo holandés',
        text: 'Los holandeses son profundamente pragmáticos. Valoran las soluciones prácticas sobre las teóricas, los resultados sobre los procesos y la eficiencia sobre la formalidad. En el trabajo, esto se traduce en: ve al grano, propón soluciones concretas y evita el exceso de burocracia o los procesos innecesariamente complejos.',
      },
      {
        subtitle: 'La bicicleta como símbolo cultural',
        text: 'La bicicleta no es solo un medio de transporte; es un símbolo de la cultura igualitaria holandesa. Reyes, ministros y directores de empresa van en bici al trabajo. Si puedes, úsala: te integrará culturalmente y es la forma más eficiente de moverse en ciudades como Ámsterdam, Utrecht o La Haya.',
      },
      {
        subtitle: 'Puntualidad y planificación',
        text: 'Los holandeses planifican con mucha antelación. Las citas sociales y profesionales se agendan con semanas de anticipación. La puntualidad es importante pero no tan estricta como en Alemania o Suiza: 5 minutos de retraso son aceptables, pero más de 10 requieren aviso previo. Improvisar reuniones de última hora no es bien recibido.',
      },
    ],
  },
  {
    id: 5,
    icon: 'ri-global-line',
    color: 'teal',
    title: 'Inglés e integración internacional',
    content: [
      {
        subtitle: 'El inglés como segunda lengua nativa',
        text: 'Los Países Bajos tienen el nivel de inglés más alto del mundo entre países no angloparlantes. En Ámsterdam y otras grandes ciudades, puedes vivir y trabajar perfectamente en inglés. Muchas empresas internacionales tienen el inglés como idioma oficial de trabajo. Esto facilita enormemente la integración inicial de profesionales extranjeros.',
      },
      {
        subtitle: 'El neerlandés como ventaja',
        text: 'Aunque el inglés es suficiente para trabajar, aprender neerlandés te abrirá puertas en empresas locales y te integrará mucho mejor en la sociedad. Los holandeses aprecian enormemente el esfuerzo por aprender su idioma, aunque rápidamente cambiarán al inglés para facilitarte la conversación. Un nivel A2-B1 ya marca una diferencia notable.',
      },
      {
        subtitle: 'Diversidad e inclusión',
        text: 'Los Países Bajos son uno de los países más tolerantes y diversos de Europa. La diversidad cultural, de género y de orientación sexual es ampliamente aceptada en el entorno laboral. Las empresas holandesas suelen tener políticas activas de diversidad e inclusión. Este ambiente cosmopolita facilita la integración de profesionales de cualquier origen.',
      },
    ],
  },
  {
    id: 6,
    icon: 'ri-heart-pulse-line',
    color: 'violet',
    title: 'Sistema social y vida en los Países Bajos',
    content: [
      {
        subtitle: 'Sistema de salud (Zorgverzekering)',
        text: 'El seguro médico es obligatorio en los Países Bajos. Debes contratar una póliza básica (basisverzekering) en los primeros 4 meses de residencia. El coste es de aproximadamente 130-160€/mes. Tu empleador puede contribuir con una compensación (zorgtoeslag). El sistema sanitario holandés es de alta calidad y muy accesible.',
      },
      {
        subtitle: 'BSN y trámites administrativos',
        text: 'El BSN (Burgerservicenummer) es tu número de identificación fiscal y social en los Países Bajos. Lo necesitas para trabajar, abrir una cuenta bancaria y acceder a servicios públicos. Se obtiene registrándose en el ayuntamiento (gemeente) de tu ciudad. Tu empleador normalmente te guiará en este proceso.',
      },
      {
        subtitle: 'Coste de vida y salarios',
        text: 'Ámsterdam es la ciudad más cara; ciudades como Eindhoven, Utrecht o Rotterdam son más asequibles. El salario mínimo en 2024 es de aproximadamente 13,27€/hora. El alquiler en Ámsterdam puede superar los 1.500€/mes para un apartamento de un dormitorio. Investiga bien el mercado inmobiliario antes de aceptar una oferta.',
      },
    ],
  },
];

const tips = [
  { icon: 'ri-check-double-line', text: 'Obtén tu BSN en el ayuntamiento en los primeros días' },
  { icon: 'ri-check-double-line', text: 'Contrata el seguro médico obligatorio en los primeros 4 meses' },
  { icon: 'ri-check-double-line', text: 'Aprende a dar y recibir feedback de forma directa y constructiva' },
  { icon: 'ri-check-double-line', text: 'Compra una bicicleta: es el transporte más eficiente y cultural' },
  { icon: 'ri-check-double-line', text: 'Agenda reuniones y citas con al menos 1-2 semanas de antelación' },
  { icon: 'ri-check-double-line', text: 'Aprende neerlandés básico aunque trabajes en inglés' },
];

const colorMap: Record<string, { bg: string; text: string; border: string; light: string }> = {
  orange: { bg: 'bg-orange-600', text: 'text-orange-600', border: 'border-orange-200', light: 'bg-orange-50' },
  emerald: { bg: 'bg-emerald-600', text: 'text-emerald-600', border: 'border-emerald-200', light: 'bg-emerald-50' },
  sky: { bg: 'bg-sky-600', text: 'text-sky-600', border: 'border-sky-200', light: 'bg-sky-50' },
  amber: { bg: 'bg-amber-600', text: 'text-amber-600', border: 'border-amber-200', light: 'bg-amber-50' },
  teal: { bg: 'bg-teal-600', text: 'text-teal-600', border: 'border-teal-200', light: 'bg-teal-50' },
  violet: { bg: 'bg-violet-600', text: 'text-violet-600', border: 'border-violet-200', light: 'bg-violet-50' },
};

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const schema = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'Claves culturales para trabajar en Países Bajos',
  description: 'Horizontalidad, consenso, equilibrio vida-trabajo y comunicación abierta en los Países Bajos. Guía para integrarse en el mercado laboral neerlandés.',
  url: `${siteUrl}/guia-claves-paises-bajos`,
  author: { '@type': 'Organization', name: 'BN ĀGENZIA' },
  publisher: { '@type': 'Organization', name: 'BN ĀGENZIA', url: siteUrl },
  inLanguage: 'es',
  about: { '@type': 'Thing', name: 'Mercado laboral Países Bajos' },
  dateModified: '2025-07-14',
};

export default function GuiaClavePaisesBajosPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 print:bg-white">
      <SeoHead
        title="Claves culturales para trabajar en Países Bajos – Guía | BN ĀGENZIA"
        description="Horizontalidad, consenso, equilibrio vida-trabajo y comunicación abierta en los Países Bajos. Todo lo que necesitas para integrarte en el mercado laboral neerlandés."
        keywords="trabajar en Países Bajos, cultura laboral holandesa, empleo Holanda, mercado laboral Países Bajos, integración profesional Holanda"
        canonical="/guia-claves-paises-bajos"
        schema={schema}
      />
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm print:hidden">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/perfil?tab=recursos')}
              className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-[#1A2B4A] transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line text-lg"></i>
              Volver a recursos
            </button>
            <div className="flex items-center gap-3">
              <span className="px-3 py-1 bg-orange-50 text-orange-700 border border-orange-200 rounded-full text-xs font-semibold">
                🇳🇱 Países Bajos
              </span>
              <button
                onClick={() => window.print()}
                className="flex items-center gap-2 px-4 py-2 bg-[#1A2B4A] text-white text-sm font-medium rounded-lg hover:bg-[#2A3B5A] transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-printer-line"></i>
                Imprimir / Guardar PDF
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-10 print:py-4 print:px-8">
        <div className="bg-gradient-to-r from-[#1A2B4A] to-[#2A3B5A] rounded-2xl p-10 mb-10">
          <div className="flex items-start gap-6">
            <div className="w-16 h-16 bg-white/10 rounded-xl flex items-center justify-center flex-shrink-0 text-4xl">
              🇳🇱
            </div>
            <div>
              <div className="flex items-center gap-3 mb-3">
                <span className="px-3 py-1 bg-orange-400/20 text-orange-300 border border-orange-400/30 rounded-full text-xs font-semibold uppercase tracking-wide">
                  Guía cultural
                </span>
                <span className="text-white/50 text-xs">6 secciones · Lectura: ~12 min</span>
              </div>
              <h1 className="text-3xl font-bold text-white mb-3 leading-tight">
                Claves culturales para trabajar en Países Bajos
              </h1>
              <p className="text-white/80 text-base leading-relaxed max-w-2xl">
                Descubre el estilo de trabajo holandés: horizontalidad, consenso, equilibrio vida-trabajo y comunicación abierta. Todo lo que necesitas para integrarte con éxito en el mercado laboral neerlandés.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 mb-10">
          <h2 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
            <i className="ri-list-check-2 text-[#1A2B4A]"></i>
            Contenido de la guía
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {sections.map((section) => {
              const c = colorMap[section.color];
              return (
                <a key={section.id} href={`#seccion-${section.id}`}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-gray-50 transition-colors group"
                >
                  <span className={`w-7 h-7 ${c.bg} text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0`}>
                    {section.id}
                  </span>
                  <span className="text-sm text-gray-700 group-hover:text-[#1A2B4A] font-medium transition-colors">
                    {section.title}
                  </span>
                </a>
              );
            })}
          </div>
        </div>

        <div className="space-y-8">
          {sections.map((section) => {
            const c = colorMap[section.color];
            return (
              <div key={section.id} id={`seccion-${section.id}`}
                className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden print:break-inside-avoid"
              >
                <div className={`${c.light} ${c.border} border-b px-6 py-5 flex items-center gap-4`}>
                  <div className={`w-11 h-11 ${c.bg} rounded-lg flex items-center justify-center flex-shrink-0`}>
                    <i className={`${section.icon} text-white text-xl`}></i>
                  </div>
                  <div>
                    <span className={`text-xs font-semibold ${c.text} uppercase tracking-wide`}>Sección {section.id}</span>
                    <h2 className="text-lg font-bold text-gray-900 leading-tight">{section.title}</h2>
                  </div>
                </div>
                <div className="p-6 space-y-6">
                  {section.content.map((item, idx) => (
                    <div key={idx} className="flex gap-4">
                      <div className={`w-1 rounded-full ${c.bg} flex-shrink-0 mt-1`} style={{ minHeight: '100%' }}></div>
                      <div>
                        <h3 className="text-sm font-bold text-gray-900 mb-1.5">{item.subtitle}</h3>
                        <p className="text-sm text-gray-600 leading-relaxed">{item.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-10 bg-white rounded-xl border border-gray-200 shadow-sm p-8 print:break-inside-avoid">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-11 h-11 bg-[#1A2B4A] rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-checkbox-multiple-line text-white text-xl"></i>
            </div>
            <div>
              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Antes de llegar</span>
              <h2 className="text-lg font-bold text-gray-900">Checklist esencial</h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {tips.map((tip, idx) => (
              <div key={idx} className="flex items-start gap-3 bg-gray-50 rounded-lg px-4 py-3 border border-gray-100">
                <i className="ri-check-double-line text-emerald-600 text-lg flex-shrink-0 mt-0.5"></i>
                <p className="text-sm text-gray-700 leading-relaxed">{tip.text}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-8 bg-gradient-to-r from-[#1A2B4A] to-[#2A3B5A] rounded-xl p-8 text-center print:hidden">
          <div className="w-14 h-14 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="ri-customer-service-2-line text-white text-2xl"></i>
          </div>
          <h3 className="text-xl font-bold text-white mb-2">¿Tienes dudas sobre el mercado holandés?</h3>
          <p className="text-white/80 text-sm mb-6 max-w-lg mx-auto leading-relaxed">
            Nuestro equipo tiene experiencia directa en el mercado laboral de los Países Bajos y puede orientarte de forma personalizada.
          </p>
          <button
            onClick={() => navigate('/#contacto')}
            className="inline-flex items-center gap-2 px-6 py-3 bg-white text-[#1A2B4A] text-sm font-semibold rounded-lg hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-mail-send-line"></i>
            Contactar con el equipo
          </button>
        </div>

        <div className="mt-8 text-center text-xs text-gray-400 pb-8 print:mt-4">
          <p>Guía elaborada por el equipo de BN Agenzia · Uso exclusivo para candidatos registrados</p>
        </div>
      </div>

      <style>{`
        @media print {
          @page { margin: 1.5cm; }
          body { font-size: 11pt; }
        }
      `}</style>
    </div>
  );
}
